import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BellIcon, BellRingIcon, CheckIcon, CheckCircleIcon, XIcon, XCircleIcon, Loader2Icon, ShieldCheckIcon, ThumbsUpIcon, ThumbsDownIcon, UserIcon, DoorOpenIcon, ClockIcon, CalendarPlusIcon, CalendarIcon, AlertCircleIcon, FileIcon, BanIcon, MegaphoneIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useInfiniteNotifications,
  useUnreadCount,
  useMarkAsRead,
  useDeleteNotification,
  Notification,
} from '@/features/notifications/hooks/useNotifications';
import { useApproveBooking, useRejectBooking, usePendingBookings } from '@/features/bookings/hooks/useAdminBooking';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { api } from '@/services/api';
import { formatRelativeDateTime, replaceMessageDate } from '../utils/relativeDateFormat';
import { useRTL } from '@/shared/hooks/useRTL';
import useMediaQuery, { MOBILE_BREAKPOINT } from '@/shared/hooks/useMediaQuery';
import { useLayout } from '@/contexts/LayoutContext';
import { BellNotificationBubble } from './BellNotificationBubble';
import {
  triggerHaptic,
  startShrinkAndSpin,
  triggerFinalPoof,
  cleanupAnimation,
  type ShrinkContext,
} from '../utils/approvalAnimations';

// Chamama color palette
const COLORS = {
  yellow: '#F4D35E',
  lightBlue: '#CBEDFF',
  tan: '#DBC299',
  pink: '#FFB6F3',
  black: '#000000',
};

/** Translate backend notification title using i18n keys, falling back to the raw title */
function translateTitle(title: string, t: ReturnType<typeof useTranslation>['t']): string {
  const cleaned = title.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
  const key = `notifications.titles.${cleaned}`;
  const translated = t(key, { defaultValue: '' });
  return translated || cleaned;
}

/**
 * NotificationBell Component
 * Bell icon with badge showing unread count and dropdown with notifications list
 */
export function NotificationBell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const isRTL = useRTL();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [_isWiggling, setIsWiggling] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalContentRef = useRef<HTMLDivElement>(null);

  // Responsive: detect mobile viewport (<700px)
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { sidebarCollapsed } = useLayout();
  const sidebarWidth = isMobile ? '0px' : sidebarCollapsed ? '5rem' : '16rem';

  // Fetch unread count for bell badge
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.data?.count || 0;

  // Fetch notifications with infinite scroll (10 per page)
  const {
    data: notificationsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteNotifications();
  const notifications = notificationsData?.pages.flatMap((p) => p.data) || [];

  // Sentinel ref for auto-loading next page on scroll
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(node);
      return () => observer.disconnect();
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // ── Mark-as-read on scroll ──────────────────────────────────
  const seenIdsRef = useRef<Set<string>>(new Set());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const mobileScrollAreaRef = useRef<HTMLDivElement>(null);

  // Debounced flush: batch-mark all newly seen IDs, then invalidate once
  const queueMarkSeen = useCallback(
    (id: string) => {
      seenIdsRef.current.add(id);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(async () => {
        const ids = Array.from(seenIdsRef.current);
        seenIdsRef.current.clear();
        if (ids.length === 0) return;
        await Promise.allSettled(ids.map((nid) => api.patch(`/notifications/${nid}/read`)));
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }, 800);
    },
    [queryClient],
  );

  // Observe notification items inside the scroll container
  useEffect(() => {
    const container = (isMobile ? mobileScrollAreaRef : scrollAreaRef).current;
    if (!container || !isOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = (entry.target as HTMLElement).dataset.notificationId;
          const unread = (entry.target as HTMLElement).dataset.unread === 'true';
          if (id && unread) queueMarkSeen(id);
        });
      },
      { root: container, threshold: 0.6 },
    );

    container.querySelectorAll('[data-notification-id]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isOpen, isMobile, notifications, queueMarkSeen]);

  // Clear pending flush on unmount
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  // Mutations
  const markAsRead = useMarkAsRead();

  const deleteNotification = useDeleteNotification();

  // Booking approval mutations (for admin/superadmin PENDING_APPROVAL notifications)
  const { hasMinimumRole } = usePermissions();
  const canApproveBookings = hasMinimumRole('OPERATOR');
  const approveBooking = useApproveBooking();
  const rejectBooking = useRejectBooking();
  // Fetch actually-pending bookings so we only show buttons for bookings still awaiting action
  const { data: pendingData } = usePendingBookings(1, 200);
  const pendingBookingIds = new Set(pendingData?.bookings?.map((b) => b.id) || []);
  // Track which notifications have been acted on (approved/declined) to hide buttons
  const [actedNotificationIds, setActedNotificationIds] = useState<Set<string>>(new Set());
  // Prevent double-clicks and dropdown close while animation is playing
  const animatingRef = useRef<boolean>(false);
  // Current animation context (for cleanup)
  const shrinkCtxRef = useRef<ShrinkContext | null>(null);
  // Ref map for notification items (needed to locate the item center for the disc)
  const itemRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  // No click-outside handler needed — overlay onClick handles closing (matches AImry/UserMenu pattern)

  // Listen for booking success events and trigger wiggle animation + bubble
  useEffect(() => {
    const handleBookingSuccess = (event: CustomEvent) => {
      // Trigger wiggle animation
      setIsWiggling(true);
      setTimeout(() => setIsWiggling(false), 2000);

      // Show bubble notification
      const message = event.detail?.message || '🎮 BOOKING SUBMITTED! Awaiting approval...';
      setBubbleMessage(message);
      setShowBubble(true);

      // Refetch notifications immediately
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    };

    window.addEventListener('booking-success', handleBookingSuccess as EventListener);

    return () => {
      window.removeEventListener('booking-success', handleBookingSuccess as EventListener);
    };
  }, [queryClient]);

  // Cleanup GSAP on unmount
  useEffect(() => {
    return () => {
      cleanupAnimation(shrinkCtxRef.current, portalContentRef.current);
    };
  }, []);

  // Check if notification is for admins
  const isAdminNotification = (notification: Notification) => {
    return notification.type === 'PENDING_APPROVAL';
  };

  // Icon per notification type
  const notificationTypeIcon = (type: Notification['type'], size: string) => {
    const cls = `${size} flex-shrink-0`;
    switch (type) {
      case 'PENDING_APPROVAL':    return <ShieldCheckIcon className={cls} />;
      case 'BOOKING_CREATED':     return <CalendarPlusIcon className={cls} />;
      case 'BOOKING_APPROVED':    return <CheckCircleIcon className={cls} />;
      case 'BOOKING_REJECTED':    return <XCircleIcon className={cls} />;
      case 'BOOKING_CANCELLED':   return <BanIcon className={cls} />;
      case 'BOOKING_REMINDER':    return <BellRingIcon className={cls} />;
      case 'SYSTEM_ANNOUNCEMENT': return <MegaphoneIcon className={cls} />;
      default:                    return <BellIcon className={cls} />;
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }

    // Navigate based on notification type
    if (notification.bookingId) {
      // For booking notifications, navigate to Weekly Calendar booking modal or admin approvals
      if (notification.link && (notification.link.includes('/admin/approvals') || notification.link.includes('/admin/pending-approvals'))) {
        // For admin pending approvals, navigate to specific booking detail (support both old and new paths)
        navigate(`/admin/approvals/${notification.bookingId}`);
      } else {
        // Navigate directly to booking modal URL (defaults to rooms calendar, booking will be found regardless)
        navigate(`/calendar/rooms/booking/${notification.bookingId}`);
      }
    } else if (notification.link) {
      // For non-booking notifications, use the link
      navigate(notification.link);
    }

    setIsOpen(false);
  };

  // Handle delete notification
  const handleDelete = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    deleteNotification.mutate(notificationId);
  };

  // Handle delete all notifications (single API call — deletes ALL, not just loaded pages)
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const handleDeleteAll = async () => {
    if (notifications.length === 0 || isDeletingAll) return;
    setIsDeletingAll(true);
    try {
      await api.delete('/notifications/all');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Shared animation handler for approve/decline (QuickBookModal-style shrink → disc → poof)
  const runApprovalAnimation = async (
    notification: Notification,
    isApprove: boolean,
    apiCall: () => Promise<unknown>,
  ) => {
    animatingRef.current = true;
    setActedNotificationIds(prev => new Set(prev).add(notification.id));
    triggerHaptic();

    const itemEl = itemRefsMap.current.get(notification.id);
    // Use the portal dropdown or mobile drawer as the element that shrinks
    const dropdownEl = portalContentRef.current;

    let ctx: ShrinkContext | null = null;

    try {
      // Phase 1 + 2: Shrink dropdown into item, pop spinning disc
      if (dropdownEl && itemEl) {
        ctx = startShrinkAndSpin(dropdownEl, itemEl, isApprove);
        shrinkCtxRef.current = ctx;
      }

      // API call runs while disc spins (with minimum display time so animation is visible)
      await Promise.all([
        apiCall(),
        new Promise(r => setTimeout(r, 1800)),
      ]);

      // Phase 3: Disc explodes → shockwave + particles + result icon
      if (ctx) {
        await triggerFinalPoof(ctx, isApprove);
      }

      // Cleanup and state update
      if (!notification.isRead) markAsRead.mutate(notification.id);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setIsOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '';
      const alreadyHandled = /cannot (approve|reject) booking with status/i.test(msg);
      if (!alreadyHandled) {
        // Only revert if it's a real error (not "already approved/rejected")
        setActedNotificationIds(prev => {
          const next = new Set(prev);
          next.delete(notification.id);
          return next;
        });
      }
      // Restore dropdown if animation was in progress
      cleanupAnimation(ctx, dropdownEl);
    } finally {
      shrinkCtxRef.current = null;
      animatingRef.current = false;
    }
  };

  // Handle approve booking from notification
  const handleApproveFromNotification = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    if (!notification.bookingId || animatingRef.current) return;
    await runApprovalAnimation(notification, true, () =>
      approveBooking.mutateAsync(notification.bookingId!),
    );
  };

  // Handle decline booking from notification
  const handleDeclineFromNotification = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    if (!notification.bookingId || animatingRef.current) return;
    const reason = window.prompt(t('notifications.declineReasonPrompt'));
    if (reason === null) return; // User cancelled
    await runApprovalAnimation(notification, false, () =>
      rejectBooking.mutateAsync({ bookingId: notification.bookingId!, reason: reason || t('notifications.declined') }),
    );
  };

  // Render notification message as structured labeled fields (Name / Room / Time …)
  const renderMessage = (message: string) => {
    // Convert any YYYY-MM-DD dates to relative form (today, yesterday, last Monday…)
    const msg = replaceMessageDate(message);

    // Icon-labeled field row
    const icons = {
      name: <UserIcon className="h-3.5 w-3.5 flex-shrink-0" />,
      room: <DoorOpenIcon className="h-3.5 w-3.5 flex-shrink-0" />,
      time: <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />,
      date: <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" />,
      reason: <AlertCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />,
      file: <FileIcon className="h-3.5 w-3.5 flex-shrink-0" />,
    };
    const field = (icon: keyof typeof icons, value: string) => (
      <span className="flex items-center gap-1.5">
        {icons[icon]}<b>{value}</b>
      </span>
    );

    // Unified order: 1) name  2) room  3) time  4) reason (optional)

    // Pattern: "{userName} requested {roomName} on {relativeDate} {timeRange}"
    const requestedMatch = msg.match(/^(.+?) requested (.+?) on (.+)$/);
    if (requestedMatch) {
      const [, userName, roomName, dateTime] = requestedMatch;
      return <>{field('name', userName)}{field('room', roomName)}{field('time', dateTime)}</>;
    }

    // Pattern: "Your booking for {roomName} on {date} …"
    const bookingForMatch = msg.match(
      /^Your booking for (.+?) on ([Tt]oday|[Yy]esterday|[Tt]omorrow|(?:(?:[Ll]ast|[Nn]ext) \w+)|\d{2}\/\d{2}\/\d{4})(.*)$/,
    );
    if (bookingForMatch) {
      const [, roomName, date, rest] = bookingForMatch;
      const reasonMatch = rest.match(/was rejected:?\s*(.+)/);
      return (
        <>
          {field('name', t('notifications.you'))}
          {field('room', roomName)}
          {field('time', date)}
          {reasonMatch && field('reason', reasonMatch[1].replace(/\.$/, ''))}
        </>
      );
    }

    // Pattern: "{userName} tagged you in a booking for {roomName} on {date}."
    const taggedMatch = msg.match(
      /^(.+?) tagged you in a booking for (.+?) on ([Tt]oday|[Yy]esterday|[Tt]omorrow|(?:(?:[Ll]ast|[Nn]ext) \w+)|\d{2}\/\d{2}\/\d{4})\.?$/,
    );
    if (taggedMatch) {
      const [, userName, roomName, date] = taggedMatch;
      return <>{field('name', userName)}{field('room', roomName)}{field('time', date)}</>;
    }

    // Pattern: "{userName} uploaded "{fileName}""
    const uploadMatch = msg.match(/^(.+?) uploaded "(.+?)"$/);
    if (uploadMatch) {
      const [, userName, fileName] = uploadMatch;
      return <>{field('name', userName)}{field('file', fileName)}</>;
    }

    return msg;
  };

  // Check if a notification should show approve/decline buttons
  const shouldShowApprovalActions = (notification: Notification) => {
    return (
      canApproveBookings &&
      notification.type === 'PENDING_APPROVAL' &&
      notification.bookingId &&
      pendingBookingIds.has(notification.bookingId) &&
      !actedNotificationIds.has(notification.id)
    );
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Bell Icon Button - Neobrutalism Style */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="neo-icon-btn relative flex items-center justify-center rounded-full"
          style={{
            width: '56px',
            height: '56px',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
          }}
          aria-label={t('notifications.bellButton')}
        >
          <BellIcon className="h-7 w-7" />
        </button>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className="absolute flex items-center justify-center text-[7px] font-bold"
            style={{
              top: '-4px',
              right: '-4px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'var(--theme-accent)',
              color: 'var(--color-text-on-primary, #000000)',
              border: '2px solid var(--theme-border)',
              boxShadow: '2px 2px 0px var(--theme-border)',
            }}
          >
            {unreadCount > 99 ? '99' : unreadCount}
          </span>
        )}

      </div>

      {/* Desktop Notifications Dropdown - Portal anchored to bell button */}
      {!isMobile && createPortal(
        <AnimatePresence>
          {isOpen && (() => {
            const rect = dropdownRef.current?.getBoundingClientRect();
            if (!rect) return null;
            const dropdownStyle: React.CSSProperties = {
              top: rect.bottom + 8,
              right: window.innerWidth - rect.right,
            };
            return (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[100]"
                  onClick={() => setIsOpen(false)}
                />
                <motion.div
                  ref={portalContentRef}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="fixed z-[101] w-96 max-w-[90vw]"
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={{
                    ...dropdownStyle,
                    backgroundColor: COLORS.yellow,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `6px 6px 0px ${COLORS.black}`,
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('notifications.title')}
                >
                  {/* Header */}
                  <div
                    className="flex items-center justify-center p-4 flex-shrink-0"
                    style={{
                      backgroundColor: COLORS.lightBlue,
                      borderBottom: `2px solid ${COLORS.black}`,
                    }}
                  >
                    <h3
                      className="text-sm font-bold uppercase"
                      style={{ color: COLORS.black }}
                    >
                      {t('notifications.title')}
                    </h3>
                  </div>

                  {/* Notifications List */}
                  <div ref={scrollAreaRef} className="flex-1 overflow-y-auto hide-scrollbar">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2Icon className="h-6 w-6 animate-spin" style={{ color: COLORS.pink }} />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="text-center py-8">
                        <BellIcon
                          className="h-12 w-12 mx-auto mb-3 opacity-30"
                          style={{ color: COLORS.black }}
                        />
                        <p className="text-sm" style={{ color: COLORS.black }}>
                          {t('notifications.noNotifications')}
                        </p>
                      </div>
                    ) : (
                      <div className="p-2">
                        {notifications.map((notification) => {
                          const isAdmin = isAdminNotification(notification);

                          return (
                            <div
                              key={notification.id}
                              ref={(el) => { if (el) itemRefsMap.current.set(notification.id, el); }}
                              data-notification-id={notification.id}
                              data-unread={(!notification.isRead).toString()}
                              onClick={() => handleNotificationClick(notification)}
                              className="flex items-start gap-3 p-3 mb-2 cursor-pointer group transition-all hover:translate-x-1"
                              style={{
                                backgroundColor: COLORS.tan,
                                border: `2px solid ${COLORS.black}`,
                                boxShadow: `2px 2px 0px ${COLORS.black}`,
                              }}
                            >

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold group-hover:underline flex items-center gap-1" style={{ color: COLORS.black }}>
                                  {notificationTypeIcon(notification.type, 'h-3.5 w-3.5')}
                                  {translateTitle(notification.title, t)}
                                </p>
                                <div className="text-xs mt-1" style={{ color: COLORS.black, opacity: 0.7 }}>
                                  {renderMessage(notification.message)}
                                </div>
                                <p className="text-[10px] mt-1.5 flex items-center gap-2" style={{ color: COLORS.black, opacity: 0.6 }}>
                                  <span>{formatRelativeDateTime(notification.createdAt)}</span>
                                  {notification.bookingId && (
                                    <>
                                      <span>•</span>
                                      <span className="font-medium">{t('notifications.booking')}</span>
                                    </>
                                  )}
                                </p>

                                {/* Approve / Decline actions for PENDING_APPROVAL notifications */}
                                {shouldShowApprovalActions(notification) && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <button
                                      type="button"
                                      onClick={(e) => handleApproveFromNotification(e, notification)}
                                      disabled={approveBooking.isPending || animatingRef.current}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                                      style={{
                                        backgroundColor: '#90EE90',
                                        color: COLORS.black,
                                        border: `2px solid ${COLORS.black}`,
                                        boxShadow: `2px 2px 0px ${COLORS.black}`,
                                      }}
                                    >
                                      <ThumbsUpIcon className="h-3.5 w-3.5" />
                                      {approveBooking.isPending ? t('notifications.approving') : t('notifications.approve')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => handleDeclineFromNotification(e, notification)}
                                      disabled={rejectBooking.isPending || animatingRef.current}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase rounded-lg transition-all active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                                      style={{
                                        backgroundColor: '#FF6B6B',
                                        color: COLORS.black,
                                        border: `2px solid ${COLORS.black}`,
                                        boxShadow: `2px 2px 0px ${COLORS.black}`,
                                      }}
                                    >
                                      <ThumbsDownIcon className="h-3.5 w-3.5" />
                                      {rejectBooking.isPending ? t('notifications.declining') : t('notifications.decline')}
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Arcade Actions — mark as read / delete */}
                              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                {!notification.isRead && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead.mutate(notification.id);
                                    }}
                                    className="p-1.5 rounded-lg transition-all active:translate-y-0.5 active:shadow-none"
                                    style={{
                                      color: COLORS.black,
                                      backgroundColor: '#90EE90',
                                      border: `2px solid ${COLORS.black}`,
                                      boxShadow: `2px 2px 0px ${COLORS.black}`,
                                    }}
                                    aria-label={t('notifications.markAsRead')}
                                    title={t('notifications.markAsRead')}
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => handleDelete(e, notification.id)}
                                  className="p-1.5 rounded-lg transition-all active:translate-y-0.5 active:shadow-none"
                                  style={{
                                    color: COLORS.black,
                                    backgroundColor: COLORS.pink,
                                    border: `2px solid ${COLORS.black}`,
                                    boxShadow: `2px 2px 0px ${COLORS.black}`,
                                  }}
                                  aria-label={t('notifications.delete')}
                                  title={t('notifications.delete')}
                                >
                                  <XIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Infinite scroll sentinel */}
                        {hasNextPage && (
                          <div ref={sentinelRef} className="flex justify-center py-3">
                            <Loader2Icon className="h-4 w-4 animate-spin" style={{ color: COLORS.black }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div
                      className="p-3 flex-shrink-0 flex items-center gap-2"
                      style={{
                        backgroundColor: COLORS.lightBlue,
                        borderTop: `2px solid ${COLORS.black}`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          navigate('/dashboard/notifications');
                          setIsOpen(false);
                        }}
                        className="flex-1 text-xs font-bold uppercase px-4 py-2.5 rounded-lg transition-all active:translate-y-0.5 active:shadow-none"
                        style={{
                          color: COLORS.black,
                          backgroundColor: COLORS.tan,
                          border: `2px solid ${COLORS.black}`,
                          boxShadow: `2px 2px 0px ${COLORS.black}`,
                        }}
                      >
                        {t('notifications.viewAll')}
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteAll}
                        disabled={isDeletingAll}
                        className="flex-1 text-xs font-bold uppercase px-4 py-2.5 rounded-lg transition-all active:translate-y-0.5 active:shadow-none disabled:opacity-50"
                        style={{
                          color: COLORS.black,
                          backgroundColor: '#FF6B6B',
                          border: `2px solid ${COLORS.black}`,
                          boxShadow: `2px 2px 0px ${COLORS.black}`,
                        }}
                      >
                        {isDeletingAll ? <Loader2Icon className="h-3.5 w-3.5 animate-spin mx-auto" /> : t('notifications.deleteAll')}
                      </button>
                    </div>
                  )}
                </motion.div>
              </>
            );
          })()}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile Full-Screen Drawer - Rendered via Portal to escape stacking context */}
      {isMobile && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]"
                onClick={() => setIsOpen(false)}
              />

            {/* Drawer - Slides from bottom with drag-to-dismiss */}
            <motion.div
              ref={portalContentRef}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              drag="y"
              dragConstraints={{ top: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                // Swipe down to dismiss (if dragged more than 100px down), but not during animations
                if (info.offset.y > 100) {
                  setIsOpen(false);
                }
              }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-0 z-[101] flex flex-col overflow-hidden"
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={t('notifications.title')}
              style={{
                height: '100dvh',
                backgroundColor: COLORS.yellow,
              }}
            >
              {/* Header (includes drag handle) */}
              <div
                className="flex flex-col items-center flex-shrink-0"
                style={{
                  backgroundColor: COLORS.lightBlue,
                  borderBottom: `4px solid ${COLORS.black}`,
                  paddingTop: 'env(safe-area-inset-top, 0.5rem)',
                }}
              >
                {/* Drag Handle */}
                <div className="pt-2 pb-1">
                  <div
                    className="w-12 h-1.5 rounded-full"
                    style={{ backgroundColor: COLORS.black, opacity: 0.3 }}
                  />
                </div>
                {/* Title + Close */}
                <div className="flex items-center justify-center relative w-full p-3">
                <h2
                  style={{
                    color: COLORS.black,
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('notifications.title')}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="absolute flex items-center justify-center w-8 h-8 rounded-full transition-transform active:scale-95"
                  style={{
                    [isRTL ? 'left' : 'right']: 12,
                    backgroundColor: COLORS.pink,
                    border: `2px solid ${COLORS.black}`,
                    boxShadow: `2px 2px 0px ${COLORS.black}`,
                  }}
                  aria-label={t('notifications.close')}
                >
                  <XIcon className="h-4 w-4" style={{ color: COLORS.black }} />
                </button>
                </div>
              </div>

              {/* Notifications List - Scrollable area */}
              <div
                ref={mobileScrollAreaRef}
                className="flex-1 p-4 overflow-y-auto hide-scrollbar"
                style={{
                  overscrollBehaviorY: 'contain',
                  paddingBottom: 'env(safe-area-inset-bottom)',
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2Icon className="h-8 w-8 animate-spin" style={{ color: COLORS.pink }} />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-12">
                    <BellIcon
                      className="h-16 w-16 mx-auto mb-4 opacity-30"
                      style={{ color: COLORS.black }}
                    />
                    <p className="text-base font-semibold" style={{ color: COLORS.black }}>
                      {t('notifications.noNotifications')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notification) => {
                      const isAdmin = isAdminNotification(notification);

                      return (
                        <div
                          key={notification.id}
                          ref={(el) => { if (el) itemRefsMap.current.set(notification.id, el); }}
                          data-notification-id={notification.id}
                          data-unread={(!notification.isRead).toString()}
                          onClick={() => handleNotificationClick(notification)}
                          className="flex items-start gap-3 p-4 cursor-pointer rounded-xl transition-all active:scale-[0.98]"
                          style={{
                            backgroundColor: COLORS.tan,
                            border: `3px solid ${COLORS.black}`,
                            boxShadow: `4px 4px 0px ${COLORS.black}`,
                          }}
                        >

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold flex items-center gap-1.5" style={{ color: COLORS.black }}>
                              {notificationTypeIcon(notification.type, 'h-4 w-4')}
                              {translateTitle(notification.title, t)}
                            </p>
                            <div className="text-sm mt-1" style={{ color: COLORS.black, opacity: 0.7 }}>
                              {renderMessage(notification.message)}
                            </div>
                            <p className="text-xs mt-2 flex items-center gap-2" style={{ color: COLORS.black, opacity: 0.6 }}>
                              <span>{formatRelativeDateTime(notification.createdAt)}</span>
                              {notification.bookingId && (
                                <>
                                  <span>•</span>
                                  <span className="font-medium">{t('notifications.booking')}</span>
                                </>
                              )}
                            </p>

                            {/* Approve / Decline actions for PENDING_APPROVAL notifications */}
                            {shouldShowApprovalActions(notification) && (
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  onClick={(e) => handleApproveFromNotification(e, notification)}
                                  disabled={approveBooking.isPending || animatingRef.current}
                                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold uppercase rounded-xl transition-all active:scale-[0.98] active:shadow-none disabled:opacity-50"
                                  style={{
                                    backgroundColor: '#90EE90',
                                    color: COLORS.black,
                                    border: `3px solid ${COLORS.black}`,
                                    boxShadow: `3px 3px 0px ${COLORS.black}`,
                                  }}
                                >
                                  <ThumbsUpIcon className="h-4 w-4" />
                                  {approveBooking.isPending ? t('notifications.approving') : t('notifications.approve')}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleDeclineFromNotification(e, notification)}
                                  disabled={rejectBooking.isPending || animatingRef.current}
                                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold uppercase rounded-xl transition-all active:scale-[0.98] active:shadow-none disabled:opacity-50"
                                  style={{
                                    backgroundColor: '#FF6B6B',
                                    color: COLORS.black,
                                    border: `3px solid ${COLORS.black}`,
                                    boxShadow: `3px 3px 0px ${COLORS.black}`,
                                  }}
                                >
                                  <ThumbsDownIcon className="h-4 w-4" />
                                  {rejectBooking.isPending ? t('notifications.declining') : t('notifications.decline')}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Mobile Actions — mark as read / delete */}
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            {!notification.isRead && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead.mutate(notification.id);
                                }}
                                className="p-2 rounded-xl transition-all active:scale-[0.98] active:shadow-none"
                                style={{
                                  color: COLORS.black,
                                  backgroundColor: '#90EE90',
                                  border: `2px solid ${COLORS.black}`,
                                  boxShadow: `2px 2px 0px ${COLORS.black}`,
                                }}
                                aria-label={t('notifications.markAsRead')}
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDelete(e, notification.id)}
                              className="p-2 rounded-xl transition-all active:scale-[0.98] active:shadow-none"
                              style={{
                                color: COLORS.black,
                                backgroundColor: COLORS.pink,
                                border: `2px solid ${COLORS.black}`,
                                boxShadow: `2px 2px 0px ${COLORS.black}`,
                              }}
                              aria-label={t('notifications.delete')}
                            >
                              <XIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Infinite scroll sentinel */}
                    {hasNextPage && (
                      <div ref={sentinelRef} className="flex justify-center py-4">
                        <Loader2Icon className="h-5 w-5 animate-spin" style={{ color: COLORS.black }} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div
                  className="sticky bottom-0 p-4 flex items-center gap-3"
                  style={{
                    backgroundColor: COLORS.lightBlue,
                    borderTop: `4px solid ${COLORS.black}`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/dashboard/notifications');
                      setIsOpen(false);
                    }}
                    className="flex-1 text-sm font-bold uppercase px-4 py-3 rounded-xl transition-all active:scale-[0.98] active:shadow-none"
                    style={{
                      color: COLORS.black,
                      backgroundColor: COLORS.tan,
                      border: `3px solid ${COLORS.black}`,
                      boxShadow: `4px 4px 0px ${COLORS.black}`,
                    }}
                  >
                    {t('notifications.viewAll')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAll}
                    disabled={isDeletingAll}
                    className="flex-1 text-sm font-bold uppercase px-4 py-3 rounded-xl transition-all active:scale-[0.98] active:shadow-none disabled:opacity-50"
                    style={{
                      color: COLORS.black,
                      backgroundColor: '#FF6B6B',
                      border: `3px solid ${COLORS.black}`,
                      boxShadow: `4px 4px 0px ${COLORS.black}`,
                    }}
                  >
                    {isDeletingAll ? <Loader2Icon className="h-4 w-4 animate-spin mx-auto" /> : t('notifications.deleteAll')}
                  </button>
                </div>
              )}
            </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Bell Notification Bubble */}
      {showBubble && (
        <BellNotificationBubble
          message={bubbleMessage}
          onClose={() => setShowBubble(false)}
          duration={5000}
        />
      )}
    </>
  );
}
